import { OrderedMap } from 'immutable';
import React, { useEffect, useState, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import SnackbarContent from '@mui/material/SnackbarContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SimplifiIcon from 'assets/logos/simplifi-logo.svg';
import type { SnackbarContentProps } from '@mui/material/SnackbarContent';

import { addNotifications, removeNotifications } from 'data/notifications/notificationsActions';
import { mkNotification } from 'data/notifications/notificationsTypes';
import { enablePushNotifications } from 'swPushNotifications';

import {
  allowPushNotificationsPromptToBeDisable,
  getActivePwaNotification,
  setActivePwaNotification,
  trackBrowserPushNotifications,
  trackPushNotifications,
  trackPushNotificationsDismissed,
  trackPushNotificationsPrompted,
} from './PwaHelpers';
import { useStyles } from './styles';

interface PushNotificationContentProps extends Omit<SnackbarContentProps, 'message'> {
  id?: string;
  onClose?: () => void;
  onDismiss: (disable: boolean) => void;
  onEnable: () => void;
}

const PushNotificationContent = forwardRef<HTMLDivElement, PushNotificationContentProps>((props, ref) => {
  const { onDismiss, onEnable } = props;
  const { classes } = useStyles();

  useEffect(() => {
    trackPushNotificationsPrompted();
  }, []);

  const allowDisable = allowPushNotificationsPromptToBeDisable();

  return (
    <SnackbarContent
      className={classes.root}
      classes={{
        message: classes.muiMessage,
      }}
      ref={ref}
      message={
        <Paper className={classes.toast} elevation={3}>
          <IconButton
            className={classes.closeButton}
            aria-label='Dismiss'
            id='snackbar-dismiss'
            type='button'
            onClick={() => onDismiss(false)}
            size='large'
          >
            <CloseIcon />
          </IconButton>
          <img className={classes.icon} src={SimplifiIcon} alt='' />
          <Typography className={classes.title} variant='bodyMedium'>
            Enable Push Notifications
          </Typography>
          <Typography className={classes.message} variant='paragraphRegular'>
            Stay updated with Quicken Simplifi alerts, even when the app is closed!
          </Typography>
          <Stack gap={1}>
            <Button fullWidth id='button-enable' onClick={() => onEnable()} variant='contained'>
              Enable
            </Button>
            <Button fullWidth variant='tonal' id='buttton-dismiss' onClick={() => onDismiss(allowDisable)}>
              Not Now
            </Button>
          </Stack>
        </Paper>
      }
    />
  );
});

PushNotificationContent.displayName = 'PushNotificationContent';

const PushNotificationPrompt = () => {
  const dispatch = useDispatch();
  const [count, setCount] = useState(1);

  useEffect(() => {
    const handleDismiss = (id: string, disable: boolean) => {
      trackPushNotificationsDismissed(disable);
      dispatch(removeNotifications([id]));
      setActivePwaNotification(null);
    };

    const handleEnable = async (id: string) => {
      trackPushNotifications();
      dispatch(removeNotifications([id]));

      const enabled = await enablePushNotifications();
      trackBrowserPushNotifications(enabled);

      setActivePwaNotification(null);
    };

    // Do not show a PWA notification if one is already being shown. But, try again in a minute for up to 5 minutes.
    // Otherwise; won't show till at least next hard refresh.
    //
    if (getActivePwaNotification()) {
      if (count < 5) {
        setInterval(() => {
          setCount(count + 1);
        }, 60000);
      }
      return;
    }
    setActivePwaNotification('PWA_PUSH_NOTIFICATIONS_PROMPT');

    const id = uuidv4();
    dispatch(
      addNotifications(
        OrderedMap([
          [
            id,
            mkNotification({
              id,
              anchorOrigin: { horizontal: 'right', vertical: 'top' },
              snackbarContent: PushNotificationContent,
              snackbarContentProps: {
                id,
                onClose: () => handleDismiss(id, false),
                onDismiss: (disable: boolean) => handleDismiss(id, disable),
                onEnable: () => handleEnable(id),
              },
              autoHideDuration: 30000,
            }),
          ],
        ]),
      ),
    );
  }, [count, dispatch]);

  return null;
};

PushNotificationPrompt.displayName = 'PushNotificaitonPrompt';

export default PushNotificationPrompt;
